import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useThemeContext } from '../contexts/ThemeContext';
import {
  useMarketplaceDefinitions,
  useDeleteMarketplaceDefinition,
  useSetMarketplaceDefinitionWorkStatus,
} from '../hooks/useMarketplaceDefinitions';
import { useUserNames } from '../hooks/useUserNames';
import { UpdatedByCell } from '../components/UpdatedByCell';
import { UserProviderChip } from '../components/UserProviderChip';
import { getCurrentProviderId } from '../utils/currentUser';
import {
  Delete as DeleteIcon,
  AccountTree as TemplateIcon,
  SubdirectoryArrowRight as ChildIcon,
  KeyboardArrowRight as CollapsedIcon,
  ExpandMore as ExpandedIcon,
} from '@mui/icons-material';

const MarketplaceDefinitions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDarkMode } = useThemeContext();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [definitionToDelete, setDefinitionToDelete] = useState<string | null>(null);

  // Initialize state from URL params
  const searchTerm = searchParams.get('search') || '';
  const verificationFilter = searchParams.get('verification') || 'all';
  const activeFilter = searchParams.get('active') || 'all';
  /** grouped = tree under templates; flat = one list so column sort is global */
  const layoutMode = searchParams.get('layout') === 'flat' ? 'flat' : 'grouped';

  // Pagination state from URL params
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '25', 10);

  // Helper to update URL params
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === null ||
        value === '' ||
        ((key === 'verification' || key === 'active') && value === 'all') ||
        (key === 'page' && value === '0') ||
        (key === 'pageSize' && value === '25')
      ) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  // Update handlers for filters
  const setSearchTerm = (value: string) => {
    updateSearchParams({ search: value, page: '0' }); // Reset to first page on search
  };

  const setVerificationFilter = (value: string) => {
    updateSearchParams({ verification: value, page: '0' });
  };

  const setActiveFilter = (value: string) => {
    updateSearchParams({ active: value, page: '0' });
  };

  const setLayoutMode = (value: 'grouped' | 'flat') => {
    updateSearchParams({
      layout: value === 'flat' ? 'flat' : null,
      page: '0',
    });
  };

  /** Template rows with children hidden in grouped view */
  const [collapsedTemplateIds, setCollapsedTemplateIds] = useState<Set<string>>(() => new Set());

  const toggleTemplateCollapse = useCallback((templateId: string) => {
    setCollapsedTemplateIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  }, []);

  // Parse sort model from URL params
  const sortModel = useMemo<GridSortModel>(() => {
    const sortField = searchParams.get('sortField');
    const sortOrder = searchParams.get('sortOrder') as 'asc' | 'desc' | null;

    if (sortField && sortOrder) {
      return [{ field: sortField, sort: sortOrder }];
    }
    // Default sort
    return [{ field: 'name', sort: 'asc' }];
  }, [searchParams]);

  // Update URL params when sort changes
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (newSortModel.length > 0) {
      const { field, sort } = newSortModel[0];
      updateSearchParams({ sortField: field, sortOrder: sort || 'asc' });
    } else {
      updateSearchParams({ sortField: null, sortOrder: null });
    }
  };

  // Handle pagination changes
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    updateSearchParams({
      page: model.page.toString(),
      pageSize: model.pageSize.toString(),
    });
  };

  // Build query string for navigation (to pass to editor)
  const getListQueryString = () => {
    return searchParams.toString();
  };

  const { data: definitions, isLoading, error, isError } = useMarketplaceDefinitions();
  const { data: userNames = {}, isLoading: userNamesLoading } = useUserNames();
  const deleteDefinition = useDeleteMarketplaceDefinition();
  const setWorkStatus = useSetMarketplaceDefinitionWorkStatus();
  const [currentProviderId, setCurrentProviderId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentProviderId().then(providerId => {
      if (!cancelled) setCurrentProviderId(providerId);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const templateChildCount = useMemo(() => {
    const m = new Map<string, number>();
    definitions?.forEach((d: any) => {
      if (d.parent_definition_id && !d.is_template) {
        const p = d.parent_definition_id;
        m.set(p, (m.get(p) || 0) + 1);
      }
    });
    return m;
  }, [definitions]);

  // Helper function to get sortable value for a field
  const getSortValue = (item: any, field: string): any => {
    switch (field) {
      case 'name':
        return item.name?.toLowerCase() || '';
      case 'overall_success_rate':
        return item.overall_success_rate || 0;
      case 'last_tested':
        return item.last_tested ? new Date(item.last_tested).getTime() : 0;
      case 'updated_at':
        return item.updated_at ? new Date(item.updated_at).getTime() : 0;
      case 'scrape_health':
      case 'maintenance_level': {
        const order: Record<string, number> = {
          untested: 0,
          needs_maintenance: 1,
          verified: 2,
        };
        const h = item.scrape_health || 'untested';
        return order[h] ?? 0;
      }
      default:
        return item[field] || '';
    }
  };

  // Filter, then either flat global sort or grouped tree (with optional collapsed children)
  const filteredDefinitions = useMemo(() => {
    if (!definitions) return [];

    const currentSort = sortModel[0] || { field: 'name', sort: 'asc' };
    const sortField = currentSort.field;
    const sortDirection = currentSort.sort === 'desc' ? -1 : 1;

    const filtered = definitions.filter((def: any) => {
      const matchesSearch =
        def.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        def.domain.toLowerCase().includes(searchTerm.toLowerCase());
      const health = def.scrape_health || 'untested';
      const matchesVerification =
        verificationFilter === 'all' ||
        (verificationFilter === 'never_run' && health === 'untested') ||
        (verificationFilter === 'verified' && health === 'verified') ||
        (verificationFilter === 'needs_maintenance' && health === 'needs_maintenance');
      const isActive = def.is_active !== false;
      const matchesActive =
        activeFilter === 'all' ||
        (activeFilter === 'active' && isActive) ||
        (activeFilter === 'inactive' && !isActive);
      return matchesSearch && matchesVerification && matchesActive;
    });

    const templateMap = new Map<string, any>();
    definitions
      .filter((d: any) => d.is_template)
      .forEach((t: any) => {
        templateMap.set(t.id, t);
      });

    const sortItems = (items: any[]) => {
      return items.sort((a, b) => {
        const aVal = getSortValue(a, sortField);
        const bVal = getSortValue(b, sortField);
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.localeCompare(bVal) * sortDirection;
        }
        if (aVal < bVal) return -1 * sortDirection;
        if (aVal > bVal) return 1 * sortDirection;
        return 0;
      });
    };

    const withParentName = (row: any) => {
      if (row.parent_definition_id && !row.is_template) {
        const parent = templateMap.get(row.parent_definition_id);
        return { ...row, _parentName: parent?.name || 'Unknown' };
      }
      return row;
    };

    if (layoutMode === 'flat') {
      const flat = filtered.map((d: any) => withParentName({ ...d }));
      sortItems(flat);
      return flat;
    }

    const templates = filtered.filter((d: any) => d.is_template);
    const children = filtered.filter((d: any) => d.parent_definition_id && !d.is_template);
    const standalone = filtered.filter((d: any) => !d.is_template && !d.parent_definition_id);

    const childrenByParent = new Map<string, any[]>();
    children.forEach((child: any) => {
      const parentId = child.parent_definition_id;
      if (!childrenByParent.has(parentId)) {
        childrenByParent.set(parentId, []);
      }
      childrenByParent.get(parentId)!.push(child);
    });

    childrenByParent.forEach(childList => {
      sortItems(childList);
    });

    const result: any[] = [];
    sortItems(templates);

    templates.forEach((template: any) => {
      result.push(template);
      if (!collapsedTemplateIds.has(template.id)) {
        const templateChildren = childrenByParent.get(template.id) || [];
        templateChildren.forEach((child: any) => {
          result.push({ ...child, _parentName: template.name });
        });
      }
      childrenByParent.delete(template.id);
    });

    childrenByParent.forEach(orphanedChildren => {
      sortItems(orphanedChildren);
      orphanedChildren.forEach((child: any) => {
        const parent = templateMap.get(child.parent_definition_id);
        result.push({ ...child, _parentName: parent?.name || 'Unknown' });
      });
    });

    sortItems(standalone);
    result.push(...standalone);

    return result;
  }, [
    definitions,
    searchTerm,
    verificationFilter,
    activeFilter,
    sortModel,
    layoutMode,
    collapsedTemplateIds,
  ]);

  const handleDeleteClick = (id: string | number) => {
    setDefinitionToDelete(String(id));
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (definitionToDelete) {
      console.log('Before delete - definitions count:', definitions?.length);

      deleteDefinition.mutate(definitionToDelete, {
        onSuccess: data => {
          console.log('Delete API response:', data);
          console.log('After delete success - definitions count:', definitions?.length);

          // Add a small delay and then check again
          setTimeout(() => {
            console.log('After delay - definitions count:', definitions?.length);
          }, 1000);

          setDeleteDialogOpen(false);
          setDefinitionToDelete(null);
        },
        onError: error => {
          console.error('Delete failed:', error);
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDefinitionToDelete(null);
  };

  const handleWorkStatus = async (definitionId: string, clear: boolean) => {
    const providerId = currentProviderId || (await getCurrentProviderId());
    if (!clear && !providerId) {
      return;
    }
    if (providerId && !currentProviderId) {
      setCurrentProviderId(providerId);
    }
    setWorkStatus.mutate({
      id: definitionId,
      workingBy: clear ? null : providerId,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Marketplace',
      flex: 2,
      minWidth: 300,
      renderCell: params => {
        const isTemplate = params.row.is_template;
        const isChild = params.row.parent_definition_id && !isTemplate;
        const parentName = params.row._parentName;
        const childCount = templateChildCount.get(params.row.id) || 0;
        const showTreeToggle = layoutMode === 'grouped' && isTemplate && childCount > 0;
        const treeCollapsed = collapsedTemplateIds.has(params.row.id);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showTreeToggle && (
              <Tooltip
                title={
                  treeCollapsed
                    ? `Show ${childCount} inherited locale(s)`
                    : `Hide ${childCount} inherited locale(s)`
                }
              >
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    toggleTemplateCollapse(String(params.row.id));
                  }}
                  sx={{ p: 0.25, flexShrink: 0 }}
                  aria-expanded={!treeCollapsed}
                  aria-label={
                    treeCollapsed ? 'Expand inherited locales' : 'Collapse inherited locales'
                  }
                >
                  {treeCollapsed ? (
                    <CollapsedIcon fontSize="small" />
                  ) : (
                    <ExpandedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {layoutMode === 'grouped' && isChild && (
              <ChildIcon
                sx={{
                  color: 'text.secondary',
                  fontSize: 18,
                  ml: 2,
                }}
              />
            )}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {params.value}
                </Typography>
                {isTemplate && (
                  <Chip
                    icon={<TemplateIcon sx={{ fontSize: '14px !important' }} />}
                    label="Template"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                  />
                )}
                {isChild && params.row.country && (
                  <Chip
                    label={params.row.country}
                    size="small"
                    color="default"
                    variant="filled"
                    sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {params.row.domain}
                {isChild && parentName && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, fontStyle: 'italic', color: 'primary.main' }}
                  >
                    (inherits from {parentName})
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'overall_success_rate',
      headerName: 'Success Rate',
      flex: 1,
      minWidth: 120,
      description:
        'Share of Collector crawl rows that parsed successfully (all tasks for this definition)',
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{(params.value * 100).toFixed(1)}%</Typography>
        </Box>
      ),
    },
    {
      field: 'last_tested',
      headerName: 'Last Tested',
      flex: 1,
      minWidth: 120,
      renderCell: params => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'Never'}
        </Typography>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      flex: 1,
      minWidth: 130,
      renderCell: params => (
        <UpdatedByCell
          updatedAt={params.row.updated_at}
          providerId={params.row.updated_by}
          displayNames={userNames}
          namesLoading={userNamesLoading}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      flex: 0.6,
      minWidth: 90,
      renderCell: params => {
        const active = params.row.is_active !== false;
        return (
          <Chip
            label={active ? 'Yes' : 'No'}
            size="small"
            color={active ? 'success' : 'default'}
            variant={active ? 'outlined' : 'filled'}
          />
        );
      },
    },
    {
      field: 'being_worked_by',
      headerName: 'Work status',
      flex: 1.2,
      minWidth: 230,
      sortable: false,
      renderCell: params => {
        const workingBy = params.row.being_worked_by as string | null | undefined;
        const isMine = Boolean(workingBy && workingBy === currentProviderId);
        const claimedAt = params.row.being_worked_at
          ? new Date(params.row.being_worked_at).toLocaleString()
          : undefined;

        if (workingBy) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Tooltip title={claimedAt ? `Claimed ${claimedAt}` : 'Being worked'}>
                <Chip
                  label={isMine ? 'Working (you)' : 'Being worked'}
                  size="small"
                  color="info"
                  variant={isMine ? 'filled' : 'outlined'}
                />
              </Tooltip>
              <UserProviderChip
                providerId={workingBy}
                displayNames={userNames}
                namesLoading={userNamesLoading}
              />
              {isMine && (
                <Tooltip title="Saving keeps this definition marked as being worked">
                  <span>
                    <Button
                      size="small"
                      onClick={() => handleWorkStatus(String(params.id), true)}
                      disabled={setWorkStatus.isLoading}
                    >
                      Clear
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        }

        return (
          <Button
            size="small"
            variant="text"
            onClick={() => handleWorkStatus(String(params.id), false)}
            disabled={!currentProviderId || setWorkStatus.isLoading}
          >
            Start work
          </Button>
        );
      },
    },
    {
      field: 'scrape_health',
      headerName: 'Scrape health',
      flex: 1,
      minWidth: 140,
      renderCell: params => {
        const h = params.row.scrape_health || 'untested';
        if (h === 'verified') {
          return <Chip label="Verified" size="small" color="success" variant="outlined" />;
        }
        if (h === 'needs_maintenance') {
          return <Chip label="Needs fix" size="small" color="warning" variant="filled" />;
        }
        return (
          <Chip
            label="Not verified"
            size="small"
            color="default"
            variant="outlined"
            sx={{ fontStyle: 'italic' }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 200,
      renderCell: params => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              // Preserve all list state params when navigating to edit
              const editUrl = `/definitions/${params.id}/edit`;
              const queryString = getListQueryString();
              navigate(`${editUrl}${queryString ? `?${queryString}` : ''}`);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteClick(params.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error loading marketplace definitions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : 'Unknown error occurred'}
        </Typography>
        <pre
          style={{
            marginTop: 16,
            padding: 16,
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            overflow: 'auto',
          }}
        >
          {JSON.stringify(error, null, 2)}
        </pre>
      </Box>
    );
  }

  if (!definitions || definitions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          No Marketplace Definitions Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No marketplace definitions are currently available. This could mean:
        </Typography>
        <ul style={{ marginTop: 8, marginBottom: 16 }}>
          <li>The database is empty</li>
          <li>There was an error loading the data</li>
          <li>The API is not responding correctly</li>
        </ul>
        <Typography variant="body2" color="text.secondary">
          Debug info: definitions = {JSON.stringify(definitions)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name or domain..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Verification</InputLabel>
                <Select
                  value={verificationFilter}
                  onChange={e => setVerificationFilter(e.target.value)}
                  label="Verification"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="never_run">Never verified</MenuItem>
                  <MenuItem value="verified">Verified (last scrape OK)</MenuItem>
                  <MenuItem value="needs_maintenance">Needs maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Active</InputLabel>
                <Select
                  value={activeFilter}
                  onChange={e => setActiveFilter(e.target.value)}
                  label="Active"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active only</MenuItem>
                  <MenuItem value="inactive">Inactive only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Table layout</InputLabel>
                <Select
                  value={layoutMode}
                  onChange={e => setLayoutMode(e.target.value as 'grouped' | 'flat')}
                  label="Table layout"
                >
                  <MenuItem value="grouped">Grouped (tree)</MenuItem>
                  <MenuItem value="flat">Flat (global sort)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  const queryString = getListQueryString();
                  navigate(`/definitions/new${queryString ? `?${queryString}` : ''}`);
                }}
              >
                Add New
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Definitions Table */}
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={filteredDefinitions}
            columns={columns}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sortingMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            getRowClassName={params => {
              const parts: string[] = [];
              if (params.row.is_template) parts.push('template-row');
              else if (params.row.parent_definition_id && layoutMode === 'grouped') {
                parts.push('child-row');
              }
              if (params.row.scrape_health === 'untested' && !params.row.is_template) {
                parts.push('untested-row');
              }
              if (params.row.being_worked_by) parts.push('being-worked-row');
              return parts.join(' ');
            }}
            sx={{
              width: '100%',
              minWidth: '100%',
              '& .MuiDataGrid-root': {
                width: '100%',
                minWidth: '100%',
              },
              '& .MuiDataGrid-main': {
                width: '100%',
                minWidth: '100%',
              },
              '& .MuiDataGrid-virtualScroller': {
                width: '100%',
                minWidth: '100%',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: isDarkMode ? 'grey.800' : 'grey.50',
                borderBottom: '2px solid',
                borderColor: isDarkMode ? 'grey.700' : 'grey.200',
                color: isDarkMode ? 'grey.100' : 'grey.900',
              },
              '& .MuiDataGrid-columnHeader': {
                color: isDarkMode ? 'grey.100' : 'grey.900',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: isDarkMode ? 'grey.100' : 'grey.900',
                fontWeight: 600,
              },
              // Template row styling
              '& .template-row': {
                backgroundColor: isDarkMode
                  ? 'rgba(25, 118, 210, 0.08)'
                  : 'rgba(25, 118, 210, 0.04)',
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(25, 118, 210, 0.15)'
                    : 'rgba(25, 118, 210, 0.08)',
                },
              },
              // Child row styling
              '& .child-row': {
                backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                },
              },
              '& .untested-row': {
                backgroundColor: isDarkMode
                  ? 'rgba(255, 167, 38, 0.12)'
                  : 'rgba(255, 152, 0, 0.08)',
                borderLeft: '3px solid',
                borderLeftColor: 'warning.light',
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 167, 38, 0.2)'
                    : 'rgba(255, 152, 0, 0.14)',
                },
              },
              '& .being-worked-row': {
                boxShadow: 'inset 4px 0 0',
                boxShadowColor: 'info.main',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mt: 3, width: '100%' }}>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Definitions
              </Typography>
              <Typography variant="h4">{definitions?.length || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Templates
              </Typography>
              <Typography variant="h4">
                {definitions?.filter((d: any) => d.is_template).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Enabled
              </Typography>
              <Typography variant="h4">
                {definitions?.filter((d: any) => d.is_active).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Verified
              </Typography>
              <Typography variant="h4">
                {definitions?.filter((d: any) => d.scrape_health === 'verified').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Need maintenance
              </Typography>
              <Typography variant="h4">
                {definitions?.filter((d: any) => d.scrape_health === 'needs_maintenance').length ||
                  0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg scrape success
              </Typography>
              <Typography variant="h4">
                {(() => {
                  const withData =
                    definitions?.filter((d: any) => d.scrape_success_rate != null) || [];
                  if (withData.length === 0) return 'N/A';
                  const avg =
                    withData.reduce((s: number, d: any) => s + (d.scrape_success_rate ?? 0), 0) /
                    withData.length;
                  return `${(avg * 100).toFixed(1)}%`;
                })()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this marketplace definition? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteDefinition.isLoading}
          >
            {deleteDefinition.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplaceDefinitions;
